<template lang="html">
  <v-layout :column="isMobile">
    <v-dialog v-model="showAssignTrucksInfoDialog"
              :full-width="!isMobile"
              max-width="500">
      <v-card class="pa-4">
        <span class="title mb-3">Assign Your Trucks</span>
        <br />
        Assign your own truck and driver to cover some or all of the remaining loads.

        <v-card-actions>
          <v-spacer />
          <v-btn color="accent" flat
                 @click="showAssignTrucksInfoDialog = false">
            Got It
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showCancelTruckInfoDialog"
              max-width="500">
      <v-card class="pa-4">
        <span class="title mb-3">Canceling Trucks</span>
        <br />
        <br />
        OK. Until we have the number adjustment buddy, nothing here has any meaning any more.
        <br />
        <br />
        Canceling a truck "unbooks" or simply removes that truck from the schedule.
        <br />
        <br />
        <v-icon color="accent">replay</v-icon>
        <strong>Cancel Booking.</strong> <em>You still intend to backfill the canceled truck.</em>
        If the customer still needs that truck, use this.
        The load will show in the planner as needing
        one additional truck.  If all trucks had been previously scheduled out, the load will
        re-appear in the planning list. This truck still needs to be covered.
        <br />
        <br />
        <v-icon color="accent">highlight_off</v-icon>
        <strong>Cancel Truck.</strong> <em>When too many trucks were scheduled.</em> If the customer
        no longer needs that truck, use this. No further action is to be taken.

        <v-card-actions>
          <v-spacer />
          <v-btn color="accent" flat
                 @click="showCancelTruckInfoDialog = false">
            Got It
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-master-detail-pane :selected="planningLoad" @reset="selectPlanningLoad(null)">
      <template slot="master">
        <v-load-nav-list search-prompt="Search load groups"
                         :title="`Planning (${unassignedLoads.length})`"
                         :accurate-since="plannedLoadsAccurateSince"
                         :loads="unassignedLoads"
                         :customerLoads="groupedBySponsorLoads"
                         :loading="loadingPlannedLoads"
                         @update:searchTerm="search = $event"
                         @refresh="getPlannedLoads">
          <template slot="subheader">
            <v-subheader class="sort-btn" @click="toggleSort()">
              <v-icon class="pr-2" color="primary">
                error_outline
              </v-icon>
              Unassigned Trucks ({{ planningLoadGroupUnassignedTruckCount }})

              <v-spacer/>
              <v-icon color="accent" class="pl-3">
                event
              </v-icon>
              <v-icon v-if="sortDesc"
                      color="accent">
                arrow_drop_down
              </v-icon>
              <v-icon v-else
                      color="accent">
                arrow_drop_up
              </v-icon>
            </v-subheader>
          </template>

          <template slot="loadTile"
                    slot-scope="{ idx, load }">
            <v-list-tile :key="`unassigned_load_${idx}_${load.listingHumanId}`"
                         @click="selectPlanningLoad(load)"
                         :class="{'primary lighten-1': isSelectedLoad(load)}"
                         :inactive="isSelectedLoad(load)"
                         ripple avatar>
              <v-list-tile-avatar>
                <v-chip :color="availableTruckCountColor(load)" text-color="white">
                  {{ planningLoadGroupAvailableTruckCount(load) }}
                </v-chip>
              </v-list-tile-avatar>

              <v-list-tile-content>
                <v-list-tile-title>{{ loadGroupName(load) }}</v-list-tile-title>
                <v-list-tile-sub-title class="text--primary">
                  {{ load.loadGroup.scheduledPickupTime | formatDay }}: {{ load.humanId }}
                </v-list-tile-sub-title>
                <v-list-tile-sub-title>
                  {{load.loadGroup.loadCount}} trucks, {{ distanceLabel(load.loadGroup.details.loadedMiles) }}
                  <span v-if="_.get(load, 'loadGroup.amount', false)">, ${{ load.loadGroup.amount }} each</span>
                </v-list-tile-sub-title>
              </v-list-tile-content>

            </v-list-tile>
            <v-divider :key="`unassigned_divider_${idx}_${load.listingHumanId}`"/>
          </template>

          <template slot="customerLoadsTile" slot-scope="skope">
            <v-list-group :key="`groupedBy_${skope.idx}`">
              <v-list-tile slot="activator">
                <v-list-tile-action>
                  <v-chip :color="availableCustomerTruckCountColor(skope.loads)"
                          text-color="white" small>
                    {{ countUnassignedTruck(skope.loads) }}
                  </v-chip>
                </v-list-tile-action>
                <v-list-tile-title>{{ skope.sponsor }}</v-list-tile-title>
              </v-list-tile>
              <div v-for="load in skope.loads" :key="'skope_' + load.humanId">
                <v-list-tile :key="'grouped_load_' + load.humanId"
                             @click="selectPlanningLoad(load)"
                             :class="{'primary lighten-1': isSelectedLoad(load)}"
                             :inactive="isSelectedLoad(load)"
                             ripple avatar>
                  <v-list-tile-avatar class="pl-4">
                    <v-chip :color="availableTruckCountColor(load)" text-color="white">
                      {{ planningLoadGroupAvailableTruckCount(load) }}
                    </v-chip>
                  </v-list-tile-avatar>

                  <v-list-tile-content>
                    <v-list-tile-title v-if="loadGroupNickname(load)">
                      {{loadGroupNickname(load)}}
                    </v-list-tile-title>
                    <v-list-tile-sub-title class="text--primary">
                      {{ load.loadGroup.scheduledPickupTime | formatDay }}: {{ load.humanId }}
                    </v-list-tile-sub-title>
                    <v-list-tile-sub-title>
                      {{load.loadGroup.loadCount}} trucks, {{ distanceLabel(load.loadGroup.details.loadedMiles) }}
                      <span v-if="_.get(load, 'loadGroup.amount', false)">, ${{ load.loadGroup.amount }}</span>
                    </v-list-tile-sub-title>
                  </v-list-tile-content>

                </v-list-tile>
                <v-divider :key="`load_divider_${load.humanId}`"/>
              </div>
            </v-list-group>
          </template>
        </v-load-nav-list>
      </template>

      <template slot="detail">
        <v-planned-load-group-details v-if="planningLoad"
                                      :planning-load="planningLoad"
                                      @reload="refreshLoad()"
                                      @onFormChange="editionFormChange($event)"
                                      @updateLoad="updatePlanningLoad($event, true)"
                                      @cancelLoad="cancelLoad($event)"
                                      :loading="loadingBtn"
                                      :company="company">
        </v-planned-load-group-details>
      </template>
    </v-master-detail-pane>

  </v-layout>
</template>

<script>
import _ from 'lodash';
import Vue2Filters from 'vue2-filters';
import API from '@/api';
import Toolbar from '@/components/Toolbar';
import moment from 'moment';
import { mapGetters } from 'vuex';
import companyNamesMixin from '@/mixins/companyNames';
import filterLoads from '@/mixins/filterLoads';
import loadGroupMixin from '@/mixins/loadGroupMixin';
import loadsRoutingMixin from '@/mixins/loadsRoutingMixin';
import bookedLoadMixin from '@/mixins/bookedLoadMixin';
import refreshPageMixin from '@/mixins/refreshPageMixin';
import planningLoadGroupsMixin from '@/mixins/planningLoadGroupsMixin';
import IconBase from '@/components/IconBase';
import CompanyName from '@/components/CompanyName';
import IconHose from '@/components/icons/IconHose';
import IconWood from '@/components/icons/IconWood';
import IconCold from '@/components/icons/IconCold';
import LoadMap from '@/components/LoadMap';
import VLoadGroupDetails from '@/components/dashboard/simple/LoadGroupDetails';
import VPlannedLoadGroupDetails from '@/components/dashboard/simple/PlannedLoadGroupDetails';
import VMasterDetailPane from '@/components/MasterDetailPane';
import VFloatField from '@/components/VFloatField';
import VLoadNavList from '@/components/dashboard/simple/LoadNavList';

import VEditLoadDetails from '@/components/InfoWidgets/EditLoadDetails';
import VBooked from './Booked';
import VDispatchRequest from './DispatchRequest';

import unitsOfMeasurePlugin from '../../../../mixins/unitsOfMeasurePlugin';

export default {
  name: 'VPlanningTool',
  mixins: [
    companyNamesMixin,
    filterLoads,
    loadGroupMixin,
    loadsRoutingMixin,
    bookedLoadMixin,
    refreshPageMixin,
    planningLoadGroupsMixin,
    Vue2Filters.mixin,
    unitsOfMeasurePlugin,
  ],
  props: {
    company: {
      type: Object,
    },
    selectedLoad: {
      type: Object,
      default: null,
    },
  },
  data: () => ({
    sortDesc: false,
    isFormDirty: false,
    loadingBtn: false,
    planningLoad: null,
    customOrigin: null,
    customDestination: null,
    loadingLoad: false,
    assignTruckCount: null,

    showAssignTrucksInfoDialog: false,
    showCancelTruckInfoDialog: false,

    dispatchPayableAmount: null,

    planingDetailTab: null,
  }),
  components: {
    VPlannedLoadGroupDetails,
    VLoadGroupDetails,
    VMasterDetailPane,
    IconBase,
    IconHose,
    IconWood,
    IconCold,
    CompanyName,
    VFloatField,
    VEditLoadDetails,
    VBooked,
    VDispatchRequest,
    VLoadNavList,
    LoadMap,
    Toolbar,
  },
  watch: {
    planningLoadGroups(list) {
      const selectedId = _.get(this.planningLoad, 'humanId') || this.selectedLoadGroupId;
      this.updatePlanningLoadById(list, selectedId);
    },
    planningLoad(newVal) {
      // planning tool is used both in the Loads component and the legacy detail screen.
      // do not update the URL history if we are on the legacy listing page, named 'listing.detail'
      if (this.$route.name === 'listing.detail') {
        return;
      }

      this.$router.push({
        params: {
          loadGroupId: _.get(newVal, 'humanId', null),
        },
      });
    },
    selectedLoad(newVal) {
      if (newVal) {
        newVal.loadGroup = this.sponsorLoadGroup(newVal.loadGroup);
      }
    }
  },
  filters: {
    formatTimeStamp(momentDate) {
      return momentDate.format('HH:mm A MMM DD');
    },
  },
  computed: {
    ...mapGetters('plannedLoads', {
      loadingPlannedLoads: 'loadingLoads',
      plannedLoadsTime: 'timestamp',
      storeCompanyId: 'companyId',
    }),
    ...mapGetters('bookedLoads', {
      bookedLoadsTime: 'timestamp',
    }),
    _() {
      return _;
    },
    plannedLoadsAccurateSince() {
      if (this.selectedLoad) {
        const modified = _.get(this.selectedLoad, 'loadGroup.modified');
        if (modified) {
          return `Last Updated ${this.$options.filters.formatTimeStamp(moment(modified))}`;
        } else {
          return 'Loading ...';
        }
      }

      if (this.loadingPlannedLoads || !this.plannedLoadsTime) {
        return 'Loading ...';
      }

      return `Updated ${this.$options.filters.formatTimeStamp(this.plannedLoadsTime)}`;
    },
    billableAmount() {
      return _.get(this.planningLoad, 'loadGroup.amount');
    },
    loadedMiles() {
      return _.get(this.planningLoad, 'loadGroup.details.loadedMiles', 'not this time');
    },
    loadedDistanceBilledRate() {
      return this.distanceRate(this.billableAmount, this.loadedMiles);
    },
    unassignedLoads() {
      if (this.selectedLoad) {
        // this.selectedLoad.loadGroup = this.sponsorLoadGroup(this.selectedLoad.loadGroup);
        return [this.selectedLoad];
      }

      const visible = this.planningLoadGroupUnassignedLoads.filter(load => this.filterListing(load));
      const sort = this.sortDesc ? 'desc' : 'asc';
      return _.orderBy(visible, [load =>
        moment(load.loadGroup.scheduledPickupTime, moment.ISO_8601).unix()], [sort]);
    },
    groupedBySponsorLoads() {
      const nameSorted = _.sortBy(this.unassignedLoads, load => load.loadGroup.details.sponsor.name);
      return _.groupBy(nameSorted, load => load.loadGroup.details.sponsor.name);
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    selectedLoadGroupId() {
      return this.loadGroupId; // from the mixin
    },
  },
  methods: {
    ...mapGetters('profiles', [
      'findCompanyAccount',
    ]),
    loadData() {
      this.getPlannedLoads();
    },
    editionFormChange(isDirty) {
      this.isFormDirty = isDirty;
    },
    getPlannedLoads() {
      this.$store.dispatch('plannedLoads/getPlannedLoads', this.company.id);
    },
    updateListing(updatedLoad) {
      this.$store.dispatch('plannedLoads/updateLoad', updatedLoad);
    },
    toggleSort() {
      this.sortDesc = !this.sortDesc;
    },
    countUnassignedTruck(loads) {
      return _.sumBy(loads, l => this.planningLoadGroupAvailableTruckCount(l));
    },
    async selectPlanningLoad(load) {
      const confirm = await this.checkPendingChanges();
      if (confirm) {
        this.updatePlanningLoad(load);
        this.resetTimer();
        this.startTimer();
      }
    },
    cancelLoad(loadId) {
      this.removeLoad(loadId);
      const load = _.cloneDeep(this.planningLoad);
      load.bookedLoads = _.reject(this.planningLoad.bookedLoads, { humanId: loadId });
      this.updatePlanningLoad(load);
    },
    updatePlanningLoadById(list, loadId) {
      if (list && list.length && loadId) {
        // Update planningLoad
        const selectedIndex = _.findIndex(list, load => load.humanId === loadId);
        this.updatePlanningLoad(selectedIndex > -1 ? list[selectedIndex] : null);
      }
    },
    updatePlanningLoad(load, updateParent) {
      if (load && this.planningLoadGroupAvailableTruckCount(load) === 0) {
        this.planningLoad = null;
      } else {
        this.planningLoad = load;
      }

      this.isFormDirty = false;

      if (updateParent) {
        this.updateListing(load);
      }
    },
    checkPendingChanges() {
      if (this.isFormDirty) {
        return this.$confirm('You have unsaved changes. Do you want to continue?', {
          color: 'warning',
          icon: 'warning',
        });
      }

      return true;
    },
    isSelectedLoad(load) {
      return load.humanId === _.get(this.planningLoad, 'humanId', null);
    },
    filterListing(load) {
      return this.planningLoadGroupAvailableTruckCount(load) > 0 &&
        (this.filterLoadGroup(load) ||
          _.some(load.bookedLoads, booked => this.filterBookedLoad(booked)) ||
          _.some(load.dispatched, dr => this.filterDispatchRequest(dr))
        );
    },
    viewListingDetail(listingId) {
      this.$router.push({ name: 'listing.detail', params: { id: listingId } });
    },
    availableCustomerTruckCountColor(loads) {
      const soonestLoad = _.minBy(loads, load => this.loadGroupHoursUntilScheduledPickup(load.loadGroup));
      return this.availableTruckCountColor(soonestLoad);
    },
    availableTruckCountColor(load) {
      const hoursUntilPickup = this.loadGroupHoursUntilScheduledPickup(load.loadGroup);
      if (hoursUntilPickup <= 36) {
        return '#BA2D0B'; // 'red darken-1';
      }

      if (hoursUntilPickup <= 96) {
        return '#EC7505'; // 'orange darken-1';
      }

      return 'primary';
    },
    onRefresh() {
      if (this.planningLoad) {
        this.refreshLoad();
      }
    },
    async refreshLoad() {
      try {
        this.loadingBtn = true;
        const apiResponse = await API.planner.getLoad(this.planningLoad.humanId);
        this.updatePlanningLoad(apiResponse.data, true);
      } catch (error) {
        this.$store.dispatch('applications/setErrorNotification', {
          message: error.message,
        });
      } finally {
        this.loadingBtn = false;
      }
    },
  },
  created() {
    const sectionKey = `${this.$route.name}_loadGroupId`;

    if (this.selectedLoad) {
      this.planningLoad = this.selectedLoad;
    } else if (this.selectedLoadGroupId || localStorage.getItem(sectionKey)) {
      this.updatePlanningLoadById(this.planningLoadGroups,
        this.selectedLoadGroupId || localStorage.getItem(sectionKey));
    }
  },
};
</script>

<style scoped>
  .gray-tile {
    background-color: #88a2aa;
  }

  .imminent {
    color: #E53935;
  }

  .sort-btn {
    cursor: pointer;
  }

  .side-list-desktop {
    height: calc(100vh - 310px);
    overflow: auto;
  }

  .main-area-desktop {
    max-height: calc(100vh - 250px);
    overflow: auto;
  }
</style>



// WEBPACK FOOTER //
// src/pages/Companies/Loads/components/PlanningTool.vue